import { Leaf, Heart, Users, Sparkles, Target, Shield } from "lucide-react";
import { PageSEO } from "@/components/SEO";

const values = [
  { icon: Leaf, title: "Growth Through Adversity", text: "We believe life's challenges are catalysts for transformation. Every setback holds the seed of a breakthrough." },
  { icon: Heart, title: "Holistic Wellness", text: "Mental health is more than coping — it's thriving. We take a whole-person approach to emotional, mental, and spiritual wellbeing." },
  { icon: Users, title: "Community First", text: "Healing happens in connection. Our community of women professionals in STEM and beyond lifts each other up." },
  { icon: Sparkles, title: "Accessible Resources", text: "Quality wellness tools shouldn't be a luxury. From free ebooks to affordable kits, we make wellbeing accessible." },
  { icon: Target, title: "Evidence-Informed", text: "Our programmes and resources are grounded in research and designed by professionals who understand burnout." },
  { icon: Shield, title: "Safe Space", text: "We create judgement-free environments where vulnerability is strength and asking for help is celebrated." },
];

const AboutPage = () => {
  return (
    <main data-testid="about-page" className="pt-20">
      <PageSEO
        title="About Us — Our Story & Values"
        description="The Lemonade Living Company was born from a belief that life's hardest moments can become your greatest turning points. We help professionals in STEM find balance and resilience."
        path="/about"
      />
      {/* Hero */}
      <section className="py-24 md:py-32 relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl">
            <p
              className="text-xs tracking-[0.3em] uppercase mb-6"
              style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}
            >
              About Us
            </p>
            <h1
              className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light leading-[1.1] mb-8"
              style={{ color: "var(--ll-text)" }}
            >
              Turning lemons into{" "}
              <em className="italic" style={{ color: "var(--ll-text-secondary)" }}>
                lemonade
              </em>
            </h1>
            <p
              className="text-base sm:text-lg leading-relaxed"
              style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}
            >
              The Lemonade Living Company was born from a simple belief: life's hardest moments
              can become your greatest turning points. We're a mental wellbeing and wellness
              company dedicated to helping professionals — especially women in STEM —
              find balance, resilience, and joy.
            </p>
          </div>
        </div>
      </section>

      {/* Image + Story */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/1320998/pexels-photo-1320998.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Fresh lemon water wellness aesthetic – The Lemonade Living Company burnout recovery and mental wellbeing resources for professionals in South Africa"
                className="rounded-2xl w-full h-[400px] object-cover"
              />
            </div>
            <div>
              <h2
                className="font-serif text-3xl sm:text-4xl font-light mb-6"
                style={{ color: "var(--ll-text)" }}
              >
                Our Story
              </h2>
              <div className="space-y-4" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>
                <p className="text-base leading-relaxed">
                  Like so many professionals, our founder experienced the devastating effects of
                  burnout firsthand. The long hours, the constant pressure, the feeling of running
                  on empty — it was a wake-up call that led to a profound transformation.
                </p>
                <p className="text-base leading-relaxed">
                  That transformation sparked The Lemonade Living Company. We understood that
                  burnout doesn't have to be the end of the story. With the right tools,
                  community, and mindset, it can be the beginning of something beautiful.
                </p>
                <p className="text-base leading-relaxed">
                  Today, we serve a growing community of professionals who are choosing
                  wellness over hustle culture, balance over burnout, and growth over
                  grinding. Whether through our free resources, comprehensive courses, or
                  curated wellness kits, we're making mental wellbeing accessible and actionable.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 md:py-32" style={{ background: "var(--ll-bg-secondary)" }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p
              className="text-xs tracking-[0.3em] uppercase mb-4"
              style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}
            >
              What We Stand For
            </p>
            <h2
              className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light"
              style={{ color: "var(--ll-text)" }}
            >
              Our{" "}
              <em className="italic" style={{ color: "var(--ll-text-secondary)" }}>
                Values
              </em>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((v, i) => {
              const Icon = v.icon;
              return (
                <div
                  key={v.title}
                  data-testid={`value-card-${v.title}`}
                  className="p-8 rounded-2xl transition-all duration-500 hover:-translate-y-1"
                  style={{ background: "white", border: "1px solid var(--ll-border)" }}
                >
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-5"
                    style={{ background: "var(--ll-bg-secondary)" }}
                  >
                    <Icon size={22} style={{ color: "var(--ll-green)" }} />
                  </div>
                  <h3
                    className="font-serif text-xl font-medium mb-3"
                    style={{ color: "var(--ll-text)" }}
                  >
                    {v.title}
                  </h3>
                  <p
                    className="text-sm leading-relaxed"
                    style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}
                  >
                    {v.text}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2
            className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light mb-6"
            style={{ color: "var(--ll-text)" }}
          >
            Ready to start your{" "}
            <em className="italic" style={{ color: "var(--ll-text-secondary)" }}>
              journey
            </em>?
          </h2>
          <p
            className="text-base max-w-lg mx-auto mb-10"
            style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}
          >
            Explore our products and take the first step toward a more balanced, fulfilling life.
          </p>
          <a
            href="/#products"
            data-testid="about-cta-products"
            className="inline-flex rounded-full px-8 py-4 text-base font-medium transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
            style={{
              background: "var(--ll-cta)",
              color: "var(--ll-cta-text)",
              fontFamily: "'Outfit', sans-serif",
            }}
          >
            Explore Products
          </a>
        </div>
      </section>
    </main>
  );
};

export default AboutPage;
